import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BrandedCard, BrandedInput, BrandedButton, BootPulse } from '../components';
import { SocialSignIn, socialProviders } from '../components/SocialSignIn';
import { useTenantContext } from '../hooks/useBranding';
import {
  kratosService,
  KratosFlow,
  KratosFlowError,
  nodeValue,
  verificationFlowOf,
} from '../services/kratosService';
import { hydraService, LoginRequest } from '../services/hydraService';
import {
  absoluteUrl,
  challengeIn,
  rememberApp,
  signInPath,
  verificationPath,
} from '../services/signInFlow';
import './Registration.css';

export const Registration: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { context, loading: contextLoading } = useTenantContext();

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [kratosAvailable, setKratosAvailable] = useState(false);
  const [flow, setFlow] = useState<KratosFlow | null>(null);
  const flowId = flow?.id ?? '';

  // Sign-up for an application's sign-in (login_challenge): once the email is
  // confirmed, the person signs in and lands back in the application. After
  // a trip Kratos drove, the challenge is in the flow's return_to instead.
  const [flowChallenge, setFlowChallenge] = useState<string | null>(null);
  const loginChallenge = searchParams.get('login_challenge') ?? flowChallenge;
  const [loginRequest, setLoginRequest] = useState<LoginRequest | null>(null);

  // Kratos sends a social sign-up back here (?flow=) when the provider didn't
  // share everything the account needs (usually the email): the flow then
  // offers the provider's "Continue" and no password.
  const finishingSocial = Boolean(
    searchParams.get('flow') &&
      flow &&
      socialProviders(flow).length > 0 &&
      !flow.ui.nodes.some((node) => node.attributes?.name === 'password')
  );

  // Check if Kratos is enabled and available
  useEffect(() => {
    const start = async () => {
      const urlChallenge = searchParams.get('login_challenge');
      const existingFlow = searchParams.get('flow');

      if (urlChallenge) {
        try {
          const request = await hydraService.getLoginRequest(urlChallenge);
          rememberApp(urlChallenge, request.client);
          setLoginRequest(request);
        } catch {
          // Not needed to sign up; the sign-in afterwards reports an expired request.
        }
      }

      if (!context?.enabled_components.includes('kratos')) return;
      const available = await kratosService.isAvailable();
      setKratosAvailable(available);
      if (!available) return;

      try {
        const loaded = existingFlow
          ? // An expired flow (flows last 10 minutes) just means starting over.
            await kratosService
              .getFlow('registration', existingFlow)
              .catch(() => kratosService.initRegistrationFlow())
          : await kratosService.initRegistrationFlow(
              // Where the email confirmation continues to — also from the
              // emailed link, in another tab.
              urlChallenge ? absoluteUrl(signInPath(urlChallenge)) : undefined
            );
        setFlow(loaded);
        if (!urlChallenge) setFlowChallenge(challengeIn(loaded.return_to));
        // What the provider did share, when finishing a social sign-up.
        setEmail((current) => current || nodeValue(loaded, 'traits.email'));
        setFirstName((current) => current || nodeValue(loaded, 'traits.name.first'));
        setLastName((current) => current || nodeValue(loaded, 'traits.name.last'));
      } catch (err) {
        // Signed-in visitors can't register again: finish the application's
        // sign-in with the account they have, or go to their account.
        if (
          err instanceof KratosFlowError &&
          err.kratosError?.error?.id === 'session_already_available'
        ) {
          navigate(urlChallenge ? signInPath(urlChallenge) : '/settings');
          return;
        }
        console.error('Failed to initialize Kratos registration flow:', err);
        setKratosAvailable(false);
      }
    };

    if (context) {
      void start();
    }
    // searchParams is stable for the life of the page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, navigate]);

  // Resolve a usable flow at submit time — the mount-time probe is async and
  // a fast submit must not race it into the (non-existent) fallback path.
  const ensureKratosFlow = async (): Promise<string | null> => {
    if (flowId) return flowId;
    if (!context?.enabled_components.includes('kratos')) return null;
    try {
      if (!(await kratosService.isAvailable())) return null;
      const started = await kratosService.initRegistrationFlow(
        loginChallenge ? absoluteUrl(signInPath(loginChallenge)) : undefined
      );
      setFlow(started);
      setKratosAvailable(true);
      return started.id;
    } catch {
      return null;
    }
  };

  // Exactly the traits identity.schema.json declares (additionalProperties:
  // false): email, name.first, name.last — empty names left out.
  const traits = () => {
    const name = {
      ...(firstName.trim() ? { first: firstName.trim() } : {}),
      ...(lastName.trim() ? { last: lastName.trim() } : {}),
    };
    return { email: email.trim(), ...(Object.keys(name).length ? { name } : {}) };
  };

  const validateForm = (): boolean => {
    if (!email || !firstName.trim() || !password || !confirmPassword) {
      setError('Email, first name and password are required');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const usableFlowId = await ensureKratosFlow();
      if (usableFlowId) {
        // Kratos registration flow (csrf + Accept handled by the service).
        // No session yet: Kratos has mailed a code to confirm the address
        // and says which verification flow it belongs to. Confirm it there,
        // then sign in — back to the application when it started here.
        const result = await kratosService.submitRegistration(usableFlowId, {
          method: 'password',
          traits: traits(),
          password,
        });
        navigate(verificationPath(verificationFlowOf(result.continue_with), loginChallenge), {
          state: { email: email.trim() },
        });
      } else {
        // Use fallback registration
        if (!context) {
          throw new Error('Tenant context not loaded');
        }

        const response = await fetch(
          `${import.meta.env.VITE_CONTROL_PLANE_API || 'http://localhost:3000'}/api/auth/register`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Tenant-ID': context.tenant_id,
            },
            body: JSON.stringify({ ...traits(), password }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Registration failed');
        }

        // Redirect to login after successful registration
        navigate('/login?registered=true');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Social sign-up leaves the SPA; Kratos brings the browser back to the
  // sign-in page (with the application's challenge and the provider) —
  // sign-up issues no session, so that page says to continue with it.
  const handleSocial = async (provider: string) => {
    setError('');
    setLoading(true);
    try {
      if (finishingSocial && flow) {
        window.location.assign(
          await kratosService.startSocial('registration', flow.id, { provider, traits: traits() })
        );
        return;
      }
      const started = await kratosService.initRegistrationFlow(
        absoluteUrl(signInPath(loginChallenge, { oidc: provider }))
      );
      window.location.assign(await kratosService.startSocial('registration', started.id, { provider }));
    } catch (err) {
      if (err instanceof KratosFlowError && err.kratosError?.error?.id === 'session_already_available') {
        navigate(loginChallenge ? signInPath(loginChallenge) : '/settings');
        return;
      }
      setError(err instanceof Error ? err.message : 'Could not start signing up with that provider.');
      setLoading(false);
    }
  };

  if (contextLoading) {
    return (
      <BrandedCard>
        <BootPulse label="Loading registration" />
      </BrandedCard>
    );
  }

  const appName = loginRequest?.client.client_name;
  // Kratos's own words on an adopted flow (e.g. what a social sign-up lacks).
  const flowMessages = searchParams.get('flow')
    ? [
        ...(flow?.ui?.messages ?? []),
        ...(flow?.ui?.nodes ?? []).flatMap((node) => node.messages ?? []),
      ].filter((message) => message.type === 'error' || message.type === 'info')
    : [];

  return (
    <BrandedCard title={finishingSocial ? 'Finish Creating Your Account' : 'Create Account'}>
      {appName && <p className="registration-info">Create your account to continue to {appName}.</p>}
      {finishingSocial && (
        <p className="registration-notice" role="status">
          Your provider didn&apos;t share everything your account needs. Fill in the rest, then
          continue.
        </p>
      )}
      {flowMessages.map((message) => (
        <div
          key={`${message.id}-${message.text}`}
          className={message.type === 'error' ? 'registration-error' : 'registration-notice'}
          role={message.type === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </div>
      ))}
      <form
        onSubmit={finishingSocial ? (e) => e.preventDefault() : handleSubmit}
        className="registration-form"
      >
        {error && (
          <div className="registration-error" role="alert">
            {error}
          </div>
        )}

        <BrandedInput
          type="email"
          name="email"
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          required
          autoComplete="email"
        />

        <BrandedInput
          type="text"
          name="first_name"
          label="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Your first name"
          required
          autoComplete="given-name"
        />

        <BrandedInput
          type="text"
          name="last_name"
          label="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Your last name"
          autoComplete="family-name"
        />

        {!finishingSocial && (
          <>
            <BrandedInput
              type="password"
              name="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password (min. 8 characters)"
              required
              autoComplete="new-password"
            />

            <BrandedInput
              type="password"
              name="confirmPassword"
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
              autoComplete="new-password"
            />

            <BrandedButton
              type="submit"
              variant="primary"
              fullWidth
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </BrandedButton>
          </>
        )}
      </form>

      <SocialSignIn
        flow={flow}
        allowed={loginRequest?.allowed_providers ?? null}
        onSelect={handleSocial}
        busy={loading}
        divider={!finishingSocial}
      />

      <div className="registration-links">
        <a href={signInPath(loginChallenge)} className="registration-link">
          Already have an account? Sign in
        </a>
      </div>

      {!kratosAvailable && context?.enabled_components.includes('kratos') && (
        <div className="registration-info">
          Using fallback registration (Kratos unavailable)
        </div>
      )}
    </BrandedCard>
  );
};
